import { PresentationType } from '~/converters/presentation-type';
import { CodeAndDescription } from '~/models/code-and-description';
import { DataType } from '~/models/data-type';
import { FilterModel } from '~/models/filter-model';
import { DateTimeProperties } from '~/utils/datetime/date-time-properties';

export enum HiddenTarget {
	FILTER = 'FILTER',
	TABLE = 'TABLE',
	EXPORT = 'EXPORT', // column is excluded from export to CSV
}

export interface ColumnDefinitionMap {
	[index: string]: ColumnDefinition;
}

export interface ColumnDefinition<FieldNameType extends string = string> {
	args?: any[];
	codeList?: CodeAndDescription[];
	isCodeListLoadedWithData?: boolean; // columns with fieldType have specially derived descriptions that are loaded with table data
	convertTo?: PresentationType;
	dataType: DataType;
	filter?: FilterModel;
	/**
	 * What's the format? date format, string format or number format?
	 * NOTE: it's wrong to define format here as `BackendTableColumnDefinition` from the backend doesn't have the property
	 * Please use TableColumnDefinition.dateTimeProperties to define date format.
	 */
	format?: string;
	hiddenFrom?: HiddenTarget[]; // if hiddenFrom is an empty array, we treat it the same as being hidden everywhere
	label: string;
	name: FieldNameType;
	render?: (...args: any[]) => string | HTMLElement;
	presentationType?: PresentationType;
	wideColumn?: boolean;
}

/**
 * Column with a value of type V and reference to the associated ColumnDefinition.
 *
 * WARNING: This has the same functionality as the Classic Form's FieldModel which does the same
 * thing on the server side.
 *
 * Performance may? be improved by lazy getting the fieldInfo with a function instead
 * @param {ColumnDefinitionMap} columnDefinitionMap: ColumnDefinitionMap with key 'columnKey'
 * @param {Object} entity: entity object with key 'columnKey'
 * @param {string} columnKey: field name (first 2 characters will be converted to upper case for field info lookup)
 */
export interface ColumnValue<V> extends ColumnDefinition {
	value: V;
}

// Representing an entity as columns and linked entities.
export type Entity<T> = { [P in keyof T]: Entity<T[P]> | ColumnValue<T[P]> };

/**
 * Use this type to assert that the passed in Object/Map has the required value.
 * T: The type of the Map/Object
 * K: The property that this object needs to contain
 */
type MapWithKey<T, K extends keyof T> = { [P in keyof T]: T[P] };
